import { zodResolver } from '@hookform/resolvers/zod';
import { Button, NumberTextField } from '@montugroup/design-system';
import ReportIcon from '@mui/icons-material/Report';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Grid, Stack } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { styled } from '@mui/styles';
import { useEffect } from 'react';
import type { FieldValues } from 'react-hook-form';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';

import FormSection from '../../FormSection';

import { CopyToClipboardButton } from '@/components/Patient/components/CopyToClipboardButton';
import { useAppStore } from '@/state-management';
import { debounce } from 'lodash';
import { usePatientIhi } from '../../hooks/usePatientIhi';
import type { IhiInformationFormFields } from './IhiInformation.schema';
import { schema } from './IhiInformation.schema';

export type IhiInformationProps = {
  editable?: boolean;
  defaultValues?: IhiInformationFormFields;
  onSubmit?: (data: IhiInformationFormFields) => void;
  isLoading?: boolean;
};

const getIhiStatusIcon = (isPatientVerified?: boolean) => (
  <InputAdornment position="start">
    {isPatientVerified ? (
      <VerifiedIcon sx={{ color: (theme) => theme.palette.success.main }} />
    ) : (
      <ReportIcon sx={{ color: (theme) => theme.palette.warning.main }} />
    )}
  </InputAdornment>
);

/*
  Using a styled component here to mimic disabled field because 'disabled=true' prop was
  causing the component to reset with 'isDirty' set to true.
*/
const StyledTextFieldDisabled = styled(TextFieldElement)({
  pointerEvents: 'none',
  '& .MuiInputLabel-root': {
    pointerEvents: 'none',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 0,
  },
});

function getIhiStatus(ihiRecordStatus?: string | null, ihiNumber?: string | null) {
  return ihiRecordStatus === 'Verified'
    ? ihiRecordStatus
    : ihiRecordStatus !== 'Verified' && ihiNumber
      ? 'Unverified'
      : 'New Patient';
}

function IhiStatus() {
  const ihiRecordStatus = useWatch({
    name: 'ihiRecordStatus',
  });

  const isPatientVerified = ihiRecordStatus === 'Verified';
  return (
    <StyledTextFieldDisabled
      name="ihiRecordStatus"
      label="Record Status"
      fullWidth
      InputProps={{
        startAdornment: getIhiStatusIcon(isPatientVerified),
      }}
    />
  );
}

export const IhiInformation = ({ editable, defaultValues, onSubmit, isLoading }: IhiInformationProps) => {
  const { verifyIhi, isPending, isError, isSuccess } = usePatientIhi();
  const getPatientIdentificationInfo = useAppStore((state) => state.getPatientIdentificationInfo());

  const updatedValues =
    getPatientIdentificationInfo.ihiRecordStatus === 'Verified' ? getPatientIdentificationInfo : defaultValues;

  const formContext = useForm<IhiInformationFormFields>({
    defaultValues,
    values: {
      ...updatedValues,
      ihiRecordStatus: getIhiStatus(updatedValues?.ihiRecordStatus, updatedValues?.ihiNumber),
    },
    resolver: zodResolver(schema),
  });

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isDirty },
  } = formContext;

  /**
   * subscribe to patient basic info to get the latest updates from the store
   * store mutations are coming from the ihi verification and patient information section (autosave)
   */
  useEffect(
    () =>
      useAppStore.subscribe(
        (state) => state.patient.identificationInfo,
        (state) => {
          formContext.reset({ ...state, ihiRecordStatus: getIhiStatus(state.ihiRecordStatus, state.ihiNumber) });
        },
      ),
    [formContext],
  );

  const isEffectivelyEditable = editable && typeof onSubmit === 'function';

  const handleOnSubmit = (formData: FieldValues) => {
    if (isDirty) {
      verifyIhi(formData.ihiNumber);
      if (onSubmit) {
        onSubmit({
          ihiNumber: formData.ihiNumber,
        });
      }
    }
  };

  return (
    <FormSection
      title="IHI Information"
      formContext={formContext}
      isLoading={isLoading}
      formSectionStatus={{ isPending, isError, isSuccess }}
      data-testid="ihi-information-section"
    >
      <FormContainer formContext={formContext}>
        <Grid container columnSpacing={10} rowSpacing={5} marginY={2} data-testid="ihi-information-form">
          <Grid item xs={12}>
            <Controller
              name="ihiNumber"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <NumberTextField
                    label="IHI Number"
                    fullWidth
                    inputProps={{
                      maxLength: 16,
                    }}
                    disabled={!isEffectivelyEditable}
                    value={value}
                    error={!!formContext.formState?.errors?.ihiNumber}
                    helperText={formContext.formState?.errors?.ihiNumber?.message?.toString() ?? ''}
                    onValueChange={(v: { value: string }) => onChange(v.value)}
                    onBlur={handleSubmit(handleOnSubmit)}
                    InputProps={{
                      endAdornment: value ? (
                        <InputAdornment position="end">
                          <CopyToClipboardButton text={value} label="Copy IHI Number" tooltipTitle="Copy IHI Number" />
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                );
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <StyledTextFieldDisabled
              name="ihiNumberStatus"
              label="Number Status"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack
              sx={{ flexDirection: { sm: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center' }}
            >
              <IhiStatus />
              <Button
                variant="outlined"
                sx={{ height: '100%', width: { xs: '100%', md: 'auto' } }}
                onClick={debounce(async () => await verifyIhi(getValues('ihiNumber')), 1000)}
              >
                Refresh
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </FormContainer>
    </FormSection>
  );
};
